import React, { useState, useEffect } from 'react';
import { ShieldAlert, Check, X, Eye, Loader2, Download } from 'lucide-react';

export default function AdminVerifications() {
  const [verifications, setVerifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDocs, setSelectedDocs] = useState(null);

  // Echte Daten laden
  useEffect(() => { fetchVerifications(); }, []);

  const fetchVerifications = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/verifications', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setVerifications(await response.json());
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  };

  // Echte Status-Änderung per API
  const handleStatusChange = async (testerId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/verifications/${testerId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setVerifications(prev => prev.map(v => v.id === testerId ? { ...v, status: newStatus } : v));
        setSelectedDocs(null);
      } else {
        alert("Fehler bei der Status-Aktualisierung.");
      }
    } catch (err) { console.error(err); }
  };

  const forceDownload = async (fileUrl, userName, suffix) => {
    if (!fileUrl) return alert("Kein Dokument vorhanden.");
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = fileUrl.split('.').pop() || 'jpg';
      link.setAttribute('download', `Ausweis_${userName.replace(/\s+/g, '_')}_${suffix}.${extension}`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Fehler beim Herunterladen der Datei.");
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 relative">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-primary">Identitätsprüfungen</h1>
          <p className="text-muted-foreground">Verifizieren Sie die Ausweise (Vorder- & Rückseite) neuer Tester.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden w-full">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-64 text-secondary"><Loader2 className="w-8 h-8 animate-spin mb-4 text-accent" /></div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-sm text-secondary">
                  <th className="p-4 font-semibold">Tester</th>
                  <th className="p-4 font-semibold">Dokumenttyp</th>
                  <th className="p-4 font-semibold">Eingereicht am</th>
                  <th className="p-4 font-semibold">Status</th>
                  <th className="p-4 font-semibold text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {verifications.length === 0 && (
                  <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">Keine Prüfungen vorhanden.</td></tr>
                )}
                {verifications.map((item) => (
                  <tr key={item.id} className="border-b border-border hover:bg-slate-50/50 transition-colors">
                    <td className="p-4"><p className="font-medium text-primary">{item.name}</p><p className="text-xs text-muted-foreground font-mono">{item.id}</p></td>
                    <td className="p-4 text-sm text-secondary">{item.type}</td>
                    <td className="p-4 text-sm text-secondary">{item.date}</td>
                    <td className="p-4">
                      {item.status === 'pending' ? <span className="flex items-center w-fit text-xs font-medium text-yellow-700 bg-yellow-100 px-2.5 py-1 rounded-full"><ShieldAlert className="w-3 h-3 mr-1" /> Ausstehend</span>
                      : item.status === 'verified' ? <span className="flex items-center w-fit text-xs font-medium text-green-700 bg-green-100 px-2.5 py-1 rounded-full"><Check className="w-3 h-3 mr-1" /> Verifiziert</span>
                      : <span className="flex items-center w-fit text-xs font-medium text-red-700 bg-red-100 px-2.5 py-1 rounded-full"><X className="w-3 h-3 mr-1" /> Abgelehnt</span>}
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => setSelectedDocs(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors flex items-center gap-1 ml-auto" title="Dokumente prüfen">
                        <Eye className="w-5 h-5" /> Prüfen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal für Ausweis-Prüfung */}
      {selectedDocs && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full flex flex-col max-h-[90vh] animate-in zoom-in-95">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <h2 className="font-bold text-primary">Ausweisdokumente von {selectedDocs.name}</h2>
              <button onClick={() => setSelectedDocs(null)} className="text-slate-400 hover:text-red-500 p-2"><X className="w-6 h-6" /></button>
            </div>
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-100 overflow-y-auto">
              <div className="flex flex-col items-center bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <span className="font-bold text-sm mb-3 text-primary">Vorderseite</span>
                <img src={selectedDocs.document_front_url} alt="Vorderseite" className="max-h-[40vh] object-contain rounded border border-slate-200 mb-4" />
                <button onClick={() => forceDownload(selectedDocs.document_front_url, selectedDocs.name, 'Vorderseite')} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Speichern
                </button>
              </div>

              <div className="flex flex-col items-center bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <span className="font-bold text-sm mb-3 text-primary">Rückseite</span>
                {selectedDocs.document_back_url ? (
                  <>
                    <img src={selectedDocs.document_back_url} alt="Rückseite" className="max-h-[40vh] object-contain rounded border border-slate-200 mb-4" />
                    <button onClick={() => forceDownload(selectedDocs.document_back_url, selectedDocs.name, 'Rückseite')} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded flex items-center gap-1">
                      <Download className="w-3.5 h-3.5" /> Speichern
                    </button>
                  </>
                ) : (
                  <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Keine Rückseite vorhanden</div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end gap-3 bg-slate-50 rounded-b-xl">
              <button onClick={() => handleStatusChange(selectedDocs.id, 'rejected')} className="px-6 py-2 text-red-600 bg-white border border-red-200 hover:bg-red-50 rounded-md font-medium">Ablehnen</button>
              <button onClick={() => handleStatusChange(selectedDocs.id, 'verified')} className="px-6 py-2 text-white bg-green-600 hover:bg-green-700 rounded-md font-medium">Verifizieren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}